import React, { useState, useEffect } from 'react';
import { ValidationRule } from '../../types/ValidationRule';
import { getKPIName } from '../../utils/kpiMapping';

interface ValidationRuleFormProps {
  rule: ValidationRule | null;
  onSubmit: (rule: ValidationRule) => void;
  onCancel: () => void;
  selectedBrand: string;
  selectedKPIs: {
    code: string;
    description: string;
    category: string;
    unit: string;
  }[];
  existingRules: ValidationRule[];
}

const ValidationRuleForm: React.FC<ValidationRuleFormProps> = ({ rule, onSubmit, onCancel, selectedBrand, selectedKPIs, existingRules }) => {
  const [ruleText, setRuleText] = useState<string>('');
  const [weight, setWeight] = useState<number>(1);
  const [error, setError] = useState<string | null>(null);

  // Calculate current weight usage for the brand (excluding current rule if editing)
  const getCurrentWeightUsage = () => {
    const brandRules = existingRules.filter(r => r.brand === selectedBrand && r.id !== rule?.id);
    return brandRules.reduce((total, r) => total + (r.weight || 0), 0);
  };

  const currentWeightUsage = getCurrentWeightUsage();
  const availableWeight = 100 - currentWeightUsage;

  useEffect(() => {
    if (rule) {
      setRuleText(rule.ruleText);
      setWeight(rule.weight || 1);
    } else {
      setRuleText('');
      setWeight(Math.min(availableWeight, 10)); // Default to 10 or available weight
    }
  }, [rule, availableWeight]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate
    if (!ruleText.trim()) {
      setError('Validation rule text is required');
      return;
    }

    if (weight < 1 || weight > availableWeight) {
      setError(`Weight must be between 1 and ${availableWeight} (available weight for this brand)`);
      return;
    }
    
    // Clear any previous errors
    setError(null);
    
    // Submit the form
    const kpiCodesData = selectedKPIs.length > 0 ? JSON.stringify(selectedKPIs.map(kpi => kpi.code)) : undefined;
    
    onSubmit({
      ...(rule ? { id: rule.id } : {}),
      ruleText: ruleText.trim(),
      brand: selectedBrand,
      weight: weight,
      kpiCodes: kpiCodesData
    });
  };

  return (
    <div className="form-container">
      <h2>{rule ? 'Edit Validation Rule' : 'Add New Validation Rule'}</h2>
      
      {/* Display selected brand and KPI */}
      <div style={{ 
        marginBottom: '20px', 
        padding: '12px 16px', 
        background: 'rgba(0, 200, 255, 0.1)', 
        borderRadius: '8px',
        border: '1px solid rgba(0, 200, 255, 0.3)'
      }}>
        <div style={{ marginBottom: '8px' }}>
          <strong>Brand:</strong> {selectedBrand}
        </div>
        {selectedKPIs.length > 0 && (
          <div>
            <strong>Selected KPIs ({selectedKPIs.length}):</strong>
            <div style={{ marginTop: '8px' }}>
              {selectedKPIs.map((kpi, index) => {
                const kpiName = getKPIName(kpi.code);
                return (
                  <div key={index} style={{
                    padding: '6px 8px',
                    marginBottom: '4px',
                    background: 'rgba(0, 200, 255, 0.1)',
                    borderRadius: '4px',
                    border: '1px solid rgba(0, 200, 255, 0.2)'
                  }}>
                    <span style={{ fontWeight: 'bold' }}>{kpiName}</span>
                    <span style={{ marginLeft: '8px', color: 'rgba(0, 0, 0, 0.7)', fontSize: '12px' }}>
                      ({kpi.category})
                    </span>
                    {kpi.unit && (
                      <div style={{ 
                        fontSize: '11px', 
                        color: 'rgba(0, 0, 0, 0.6)', 
                        marginTop: '2px' 
                      }}>
                        Unit: {kpi.unit}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="ruleText">Validation Rule</label>
          <input
            type="text"
            id="ruleText"
            value={ruleText}
            onChange={(e) => setRuleText(e.target.value)}
            placeholder="Enter validation rule text"
            autoFocus
          />
        </div>

        {/* Weight Usage Display */}
        <div style={{
          padding: '12px 16px',
          background: availableWeight <= 10 ? 'rgba(255, 100, 100, 0.1)' : 'rgba(0, 200, 255, 0.1)',
          borderRadius: '8px',
          border: `1px solid ${availableWeight <= 10 ? 'rgba(255, 100, 100, 0.3)' : 'rgba(0, 200, 255, 0.3)'}`,
          marginBottom: '15px'
        }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>
            Weight Usage for {selectedBrand}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
            <span>Used: {currentWeightUsage}/100</span>
            <span>Available: {availableWeight}</span>
          </div>
          <div style={{
            width: '100%',
            height: '8px',
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '4px',
            marginTop: '8px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${currentWeightUsage}%`,
              height: '100%',
              background: currentWeightUsage > 90 ? '#ff6b6b' : currentWeightUsage > 70 ? '#ffa726' : '#4fc3f7',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="weight">Rule Weight</label>
          <input
            type="number"
            id="weight"
            value={weight}
            onChange={(e) => setWeight(Math.max(1, Math.min(availableWeight, parseInt(e.target.value) || 1)))}
            min="1"
            max={availableWeight}
            placeholder="Enter rule weight"
          />
          <div style={{ 
            fontSize: '12px', 
            color: 'rgba(0, 0, 0, 0.6)', 
            marginTop: '4px' 
          }}>
            Weight must be between 1 and {availableWeight}. Higher weights indicate more important rules.
          </div>
        </div>
        
        <div className="form-actions">
          <button 
            type="button" 
            className="secondary-button" 
            onClick={onCancel}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="primary-button"
          >
            {rule ? 'UPDATE' : 'CREATE'} VALIDATION RULE
          </button>
        </div>
      </form>
    </div>
  );
};

export default ValidationRuleForm; 